import React, { Fragment, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Webcam from "react-webcam";
import { Dialog, Transition } from "@headlessui/react";
import PartialLoading from "../partials/PartialLoading";
import camera from "../../assets/images/camera.svg";
// NOTE: it will accept base64 image as value

const FieldCameraInput = ({
  id,
  field,
  form: { touched, errors, setFieldValue },
  isFileUpload = false,
}) => {
  const { value } = field;
  const [isOpen, _toggleOpen] = useState(false);
  const webcamRef = React.useRef(null);
  const imageRef = useRef(null);
  const imageNameRef = React.useRef(null);
  const [imgSrc, setImgSrc] = React.useState(null);
  const [isCapture, setIsCapture] = useState(false);
  const [hasPermission, _setHasPermission] = useState(undefined);
  const [hasWebCam, _setHasWebCam] = useState(undefined);
  const [checkingConfig, _setCheckingConfig] = useState(true);
  const [avatarName, _avatarName] = useState("Avatar");
  useEffect(() => {
    if (isOpen) {
      navigator.mediaDevices
        .getUserMedia({
          audio: false,
          video: true,
        })
        .then(successCallback, errorCallback);
    }
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      const photoArray = value?.split("/");
      if (photoArray.length > 0) {
        const photoName = photoArray[photoArray?.length - 1] || "Photograph";
        if (field.value !== imgSrc) {
          setImgSrc(field.value);
          imageNameRef.current = photoName;
        }
      }
    }
  }, [value]);

  useEffect(() => {
    if (value) {
      const photoArray = value?.split("/");
      if (photoArray.length) {
        const photoName = photoArray[photoArray?.length - 1] || "Photograph";
        if (photoName !== imageNameRef.current) {
          _avatarName("Photograph");
        } else {
          _avatarName(photoName);
        }
      }
    }
  }, [value]);
  const successCallback = function () {
    _setHasPermission(true);

    navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
      let hasCam = false;
      hasCam = mediaDevices.some(({ kind }) => kind === "videoinput");

      _setHasWebCam(hasCam);
      _setCheckingConfig(false);
    });
  };
  const errorCallback = function (error) {
    _setCheckingConfig(false);
    if (error.name === "NotAllowedError") {
      _setHasPermission(false);
    } else {
      _setHasWebCam(false);
    }
  };

  const saveImage = () => {
    setFieldValue(field.name, imgSrc);
    setIsCapture(false);
    _toggleOpen(false);
  };

  const capture = React.useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setIsCapture(true);
    setImgSrc(imageSrc);
  }, [webcamRef, setImgSrc]);

  const cancel = () => {
    setImgSrc(field.value);
    _toggleOpen(false);
  };

  const handleOpen = () => {
    _toggleOpen(true);
  };
  const renderCheckingConfig = () => {
    return (
      <div className={"flex justify-center items-center"}>
        <PartialLoading />
        <p>Checking camera configurations</p>
      </div>
    );
  };

  const renderNoPermission = (
    <div className={"flex justify-center items-center text-center"}>
      <p>You didn&apos;t allowed permission to access camera.</p>
    </div>
  );

  const renderNoCam = (
    <div className={"flex justify-center items-center text-center"}>
      <p>
        You don&apos;t have any webcam integrated. Please check and try again.
      </p>
    </div>
  );

  const noPermission =
    !checkingConfig && typeof hasPermission === "boolean" && !hasPermission;
  const noWebCam =
    !checkingConfig && typeof hasWebCam === "boolean" && !hasWebCam;

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleImageUpload = async (e) => {
    const imgSrc = URL.createObjectURL(e.target.files[0]);
    const base64 = await toBase64(e.target.files[0]);
    setImgSrc(imgSrc);
    setFieldValue(field.name, base64);
  };

  const removeImageValue = () => {
    imageRef.current.value = "";
  };
  return (
    <div className={"mb-8"}>
      <div className="field fieldCameraInput grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-2">
        <div
          className="cameraImgPreview h-24 w-full  bg-white shadow rounded relative"
          onClick={() => handleOpen()}
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              className="w-full h-full object-fill rounded-lg"
              alt="capture image"
            />
          ) : (
            <div className="flex justify-center items-center h-full">
              <i className={`text-7xl icon icon-user text-gray-400`}></i>
            </div>
          )}
          <img
            src={camera}
            className="absolute bottom-0 right-0 h-8 translate-x-1 translate-y-1"
          />
        </div>
        <div className="w-100 col-span-2">
          <p className={`py-2 px-4 w-full break-all col-span-2 h-fit max-h-lg shadow rounded bg-white text-slate-600`}>
          {field.value ? "Image uploaded successfully" : "No image uploaded"}
          </p>
          {isFileUpload && (
            <>
              <div className="w-100 flex justify-center my-3">OR</div>
              <div className="field fieldNewFileInput grid grid-cols-1 md:grid-cols-1 gap-6 md:gap-8 mb-2">
                <label
                  htmlFor={id}
                  className={"w-full p-2 bg-white shadow rounded hover:bg-gray-100"}
                >
                  <input
                    type={"file"}
                    name={field.name}
                    id={id}
                    accept="image/png, image/jpeg"
                    onChange={(e) => handleImageUpload(e)}
                    ref={imageRef}
                  />
                  <span>upload File</span>
                  {/*<span onClick={()=>removeImageValue()}>X</span>*/}
                </label>
              </div>
            </>
          )}
        </div>
      </div>
      {touched[field.name] && (
        <p className="text-red-500 text-xs italic">{errors[field.name]}</p>
      )}

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto"
          onClose={() => _toggleOpen(false)}
        >
          <div className="min-h-screen px-4 text-center">
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <Dialog.Title as="h3">
                  <div className="flex justify-between items-center pb-3 mb-3 border-b border-slate-200">
                    <span className="text-xl  font-['Poppins-Bold'] leading-6 text-gray-900 flex-1">
                      Capture Image
                    </span>
                    <div onClick={cancel}>
                      <i className="icon icon-x  text-xl mx-1 cursor-pointer"></i>
                    </div>
                  </div>
                </Dialog.Title>
                {isOpen && (
                  <div className="mt-2">
                    {checkingConfig && renderCheckingConfig()}
                    {noPermission && renderNoPermission}
                    {noWebCam && renderNoCam}
                    {!checkingConfig && !noPermission && !noWebCam && (
                      <>
                        <div className={"flex flex-wrap justify-around"}>
                          <Webcam
                            audio={false}
                            width={200}
                            ref={webcamRef}
                            screenshotQuality={1}
                            screenshotFormat="image/jpeg"
                          />
                          {imgSrc && (
                            <Transition.Child
                              as={Fragment}
                              enter="ease-out duration-300"
                              enterFrom="opacity-0 scale-95"
                              enterTo="opacity-100 scale-100"
                            >
                              <div className={"mt-2 md:mt-0 previewImage"}>
                                <img
                                  src={imgSrc}
                                  alt="previewImage"
                                  width={150}
                                />
                              </div>
                            </Transition.Child>
                          )}
                        </div>
                        <div
                          className={
                            "my-3 flex flex-wrap gap-2 justify-around item-center"
                          }
                        >
                          <button
                            type={"button"}
                            className={
                              "w-full md:w-fit justify-center py-1 px-2 mr-2 text-white inline-flex items-center bg-black rounded-md hover:bg-zinc-700"
                            }
                            onClick={capture}
                          >
                            {imgSrc ? (
                              <>
                                <i className="icon icon-loop text-md mx-2 "></i>
                                <span className="mx-2">Retake</span>
                              </>
                            ) : (
                              <>
                                <i className="icon icon-camera text-md mx-2 "></i>
                                <span className="mx-2">Capture</span>
                              </>
                            )}
                          </button>

                          {imgSrc && (
                            <Transition.Child
                              as={Fragment}
                              enter="ease-out duration-300"
                              enterFrom="opacity-0 scale-95"
                              enterTo="opacity-100 scale-100"
                            >
                              <button
                                type={"button"}
                                className={
                                  "w-full md:w-fit justify-center py-1 px-2 mr-2 text-white inline-flex items-center bg-black rounded-md hover:bg-zinc-700"
                                }
                                onClick={saveImage}
                                disabled={!isCapture}
                              >
                                <i className="icon icon-floppy text-lg mx-2 "></i>
                                <span className="mx-2">Confirm</span>
                              </button>
                            </Transition.Child>
                          )}

                          {/* <button
                            type={"button"}
                            className={
                              "w-full md:w-fit justify-center py-1 px-2 mr-2 text-white inline-flex items-center  rounded-md border-2 border-zinc-500 text-zinc-500 hover:text-zinc-700 hover:border-zinc-700"
                            }
                            
                          >
                            <i className="icon icon-x  text-xl mx-1 "></i>
                            <span className="mx-1">Cancel</span>
                          </button> */}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default FieldCameraInput;

FieldCameraInput.propTypes = {
  id: PropTypes.string,
  field: PropTypes.object,
  form: PropTypes.object,
  isFileUpload: PropTypes.bool,
};